import { Box } from "@mui/material";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchFilters, FilterChipDef } from "./filterChipDefs";
import { ThemeMode } from "../../theme/ThemeContext";
import { useFilterDisplayLabel } from "../../hooks/useFilterDisplayLabel";
import {
  useTypeaheadSearch,
  useInteractionTags,
  useDirectionList,
} from "../../../hooks/useInteraction";
import { useSites } from "../../../hooks/useSite";
import RetentionPopover from "./popovers/RetentionPopover";
import { useParams } from "react-router-dom";
import LoadingIndicator from "../../../components/common/LoadingIndicator/LoadingIndicator";

// ---------------------------------------------------------------------------
// Shared popover colors (portal-rendered, outside V2 theme div)
// ---------------------------------------------------------------------------

function popoverColors(_mode: ThemeMode) {
  return {
    bg: "var(--bg-raised)",
    border: "var(--border-mid)",
    shadow: "0 8px 24px rgba(0,0,0,0.5)",
    text: "var(--text-primary)",
    textSecondary: "var(--text-secondary)",
    inputBg: "var(--bg-surface)",
    inputBorder: "var(--border-mid)",
    accent: "var(--accent)",
    accentDim: "var(--accent-dim)",
    hover: "var(--bg-hover)",
    muted: "var(--text-muted)",
  };
}

// ---------------------------------------------------------------------------
// Shared input style
// ---------------------------------------------------------------------------

function inputSx(c: ReturnType<typeof popoverColors>) {
  return {
    width: "100%",
    px: "10px",
    py: "7px",
    fontSize: "0.8125rem",
    fontFamily: "inherit",
    color: c.text,
    backgroundColor: c.inputBg,
    border: `1px solid ${c.inputBorder}`,
    borderRadius: "6px",
    outline: "none",
    boxSizing: "border-box" as const,
    "&:focus": { borderColor: c.accent },
    "&::placeholder": { color: c.muted },
  };
}

function btnSx(c: ReturnType<typeof popoverColors>, primary = false) {
  return {
    px: "12px",
    py: "5px",
    fontSize: "0.75rem",
    fontWeight: 600,
    fontFamily: "inherit",
    borderRadius: "6px",
    cursor: "pointer",
    border: primary ? "none" : `1px solid ${c.inputBorder}`,
    backgroundColor: primary ? c.accent : "transparent",
    color: primary ? "#fff" : c.text,
    "&:hover": {
      backgroundColor: primary ? c.accent : c.hover,
      opacity: primary ? 0.9 : 1,
    },
  };
}

// ---------------------------------------------------------------------------
// Popover content props
// ---------------------------------------------------------------------------

export interface FilterPopoverContentProps {
  chipDef: FilterChipDef;
  filters: SearchFilters;
  onFiltersChange: (f: SearchFilters) => void;
  onClose: () => void;
  mode: ThemeMode;
  customerId?: string;
}

type PopoverBaseProps = Omit<FilterPopoverContentProps, "chipDef">;
type PopoverWithChipDef = PopoverBaseProps & { chipDef: FilterChipDef };

// ---------------------------------------------------------------------------
// Shared popover sub-components
// ---------------------------------------------------------------------------

type Colors = ReturnType<typeof popoverColors>;

function SectionLabel({ c, children }: { c: Colors; children: ReactNode }) {
  return (
    <Box
      component="label"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: c.textSecondary,
      }}
    >
      {children}
    </Box>
  );
}

function PopoverFooter({
  c,
  onClear,
  onApply,
}: {
  c: Colors;
  onClear: () => void;
  onApply: () => void;
}) {
  const { t } = useTranslation(["common"]);
  return (
    <Box
      sx={{
        display: "flex",
        gap: "8px",
        justifyContent: "flex-end",
        mt: "4px",
      }}
    >
      <Box component="button" onClick={onClear} sx={btnSx(c)}>
        {t("common:clear")}
      </Box>
      <Box component="button" onClick={onApply} sx={btnSx(c, true)}>
        {t("common:apply")}
      </Box>
    </Box>
  );
}

interface Suggestion {
  value: string;
  friendlyValue?: string;
}

function SuggestionList({
  c,
  suggestions,
  onSelect,
}: {
  c: Colors;
  suggestions: Suggestion[];
  onSelect: (value: string) => void;
}) {
  return (
    <Box
      sx={{
        maxHeight: 160,
        overflowY: "auto",
        borderRadius: "6px",
        border: `1px solid ${c.inputBorder}`,
        bgcolor: c.inputBg,
      }}
    >
      {suggestions.map((s) => (
        <Box
          key={s.value}
          component="button"
          onClick={() => onSelect(s.friendlyValue || s.value)}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            px: "10px",
            py: "6px",
            fontSize: "0.8125rem",
            color: c.text,
            bgcolor: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            "&:hover": { bgcolor: c.hover },
          }}
        >
          {s.friendlyValue || s.value}
        </Box>
      ))}
    </Box>
  );
}

function OptionButton({
  c,
  isActive,
  onClick,
  children,
}: {
  c: Colors;
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        px: "10px",
        py: "7px",
        fontSize: "0.8125rem",
        fontFamily: "inherit",
        color: isActive ? c.accent : c.text,
        bgcolor: isActive ? c.accentDim : "transparent",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: isActive ? 600 : 400,
        "&:hover": { bgcolor: c.hover },
      }}
    >
      {children}
    </Box>
  );
}

function TypeaheadInput({
  c,
  value,
  setValue,
  showSuggestions,
  setShowSuggestions,
  suggestions,
  onSelectSuggestion,
  placeholder,
}: {
  c: Colors;
  value: string;
  setValue: (v: string) => void;
  showSuggestions: boolean;
  setShowSuggestions: (b: boolean) => void;
  suggestions: Suggestion[] | undefined;
  onSelectSuggestion: (value: string) => void;
  placeholder: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <>
      <Box
        ref={inputRef}
        component="input"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        sx={inputSx(c)}
      />
      {showSuggestions && suggestions && suggestions.length > 0 && (
        <SuggestionList
          c={c}
          suggestions={suggestions}
          onSelect={onSelectSuggestion}
        />
      )}
    </>
  );
}

function MultiSelectShell({
  c,
  searchPlaceholder,
  search,
  setSearch,
  onClear,
  onApply,
  children,
}: {
  c: Colors;
  searchPlaceholder: string;
  search: string;
  setSearch: (v: string) => void;
  onClear: () => void;
  onApply: () => void;
  children: ReactNode;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        p: "14px",
        minWidth: 240,
      }}
    >
      <Box
        ref={searchRef}
        component="input"
        type="text"
        value={search}
        placeholder={searchPlaceholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        sx={inputSx(c)}
      />
      <Box
        sx={{
          maxHeight: 200,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        {children}
      </Box>
      <PopoverFooter c={c} onClear={onClear} onApply={onApply} />
    </Box>
  );
}

function CheckboxRow({
  c,
  checked,
  onChange,
  label,
}: {
  c: Colors;
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
}) {
  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "10px",
        py: "5px",
        fontSize: "0.8125rem",
        color: c.text,
        cursor: "pointer",
        borderRadius: "4px",
        "&:hover": { bgcolor: c.hover },
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        sx={{ accentColor: c.accent }}
      />
      {label}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Date popover
// ---------------------------------------------------------------------------

function DatePopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const toDatetimeLocal = (iso: string | undefined) => {
    if (!iso) return "";
    // Accept either "YYYY-MM-DD" or a full ISO string; truncate to "YYYY-MM-DDTHH:mm"
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [from, setFrom] = useState(toDatetimeLocal(filters.from));
  const [to, setTo] = useState(toDatetimeLocal(filters.to));
  const fromRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    fromRef.current?.focus();
  }, []);

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      from: from ? `${from}:00.000Z` : undefined,
      to: to ? `${to}:59.999Z` : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, from: undefined, to: undefined });
    onClose();
  };

  const dateInputSx = {
    ...inputSx(c),
    colorScheme: mode === "dark" ? "dark" : "light",
    "&::-webkit-calendar-picker-indicator": {
      filter: mode === "dark" ? "invert(1)" : "none",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        p: "14px",
        minWidth: 260,
      }}
    >
      <SectionLabel c={c}>{t("common:from")}</SectionLabel>
      <Box
        ref={fromRef}
        component="input"
        type="datetime-local"
        value={from}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFrom(e.target.value)
        }
        sx={dateInputSx}
      />
      <SectionLabel c={c}>{t("common:to")}</SectionLabel>
      <Box
        component="input"
        type="datetime-local"
        value={to}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTo(e.target.value)
        }
        sx={dateInputSx}
      />
      <PopoverFooter c={c} onClear={handleClear} onApply={handleApply} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Agent typeahead popover
// ---------------------------------------------------------------------------

function AgentPopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode, customerId } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [input, setInput] = useState(filters.agentName || "");
  const [exactMatch, setExactMatch] = useState(
    filters.agentNameExactMatch || false,
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestions } = useTypeaheadSearch(
    "agentName",
    "AgentName",
    input,
    customerId,
  );

  const handleSelect = (value: string) => {
    setInput(value);
    setShowSuggestions(false);
  };

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      agentName: input || undefined,
      agentNameExactMatch: input ? exactMatch : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({
      ...filters,
      agentName: undefined,
      agentNameExactMatch: undefined,
    });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "14px",
        minWidth: 260,
        position: "relative",
      }}
    >
      <SectionLabel c={c}>{t("search:agentName")}</SectionLabel>
      <TypeaheadInput
        c={c}
        value={input}
        setValue={setInput}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        suggestions={suggestions}
        onSelectSuggestion={handleSelect}
        placeholder={t("search:typeToSearch")}
      />
      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "0.75rem",
          color: c.textSecondary,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box
          component="input"
          type="checkbox"
          checked={exactMatch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setExactMatch(e.target.checked)
          }
          sx={{ accentColor: c.accent }}
        />
        {t("search:exactMatch")}
      </Box>
      <PopoverFooter c={c} onClear={handleClear} onApply={handleApply} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Phone typeahead popover
// ---------------------------------------------------------------------------

function PhonePopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode, customerId } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [input, setInput] = useState(filters.phoneNumber || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: suggestions } = useTypeaheadSearch(
    "phoneNumber",
    "PhoneNumber",
    input,
    customerId,
  );

  const handleSelect = (value: string) => {
    setInput(value);
    setShowSuggestions(false);
  };

  const handleApply = () => {
    onFiltersChange({ ...filters, phoneNumber: input || undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, phoneNumber: undefined });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "14px",
        minWidth: 260,
      }}
    >
      <SectionLabel c={c}>{t("search:phoneNumber")}</SectionLabel>
      <TypeaheadInput
        c={c}
        value={input}
        setValue={setInput}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        suggestions={suggestions}
        onSelectSuggestion={handleSelect}
        placeholder={t("search:typeToSearch")}
      />
      <PopoverFooter c={c} onClear={handleClear} onApply={handleApply} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Select popover (Type, Direction)
// ---------------------------------------------------------------------------

interface SelectOption {
  value: string;
  label: string;
}

const TYPE_OPTIONS: SelectOption[] = [
  { value: "Audio", label: "audio" },
  { value: "Video", label: "video" },
  { value: "AudioAndVideo", label: "audioAndVideo" },
  { value: "Chat", label: "chat" },
  { value: "Email", label: "email" },
];

function SelectPopover(props: PopoverWithChipDef) {
  const { chipDef, filters, onFiltersChange, onClose, mode } = props;
  const { customerId } = useParams();
  const { data: directionOptions, isLoading: areDirectionOptionsLoading } =
    useDirectionList(customerId);
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const options = useMemo(
    () =>
      chipDef.id === "direction"
        ? (directionOptions?.map(
            (directionOption): SelectOption => ({
              label: directionOption,
              value: directionOption,
            }),
          ) ?? [])
        : TYPE_OPTIONS,
    [chipDef.id, directionOptions],
  );
  const filterKey =
    chipDef.id === "direction" ? "direction" : "interactionType";
  const current = filters[filterKey as keyof SearchFilters] as
    | string
    | undefined;

  const handleSelect = (value: string) => {
    onFiltersChange({ ...filters, [filterKey]: value });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, [filterKey]: undefined });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        p: "8px",
        minWidth: 180,
      }}
    >
      {areDirectionOptionsLoading && (
        <Box sx={{ px: "10px", py: "7px" }}>
          <LoadingIndicator />
        </Box>
      )}
      {options.map((opt) => (
        <OptionButton
          key={opt.value}
          c={c}
          isActive={current === opt.value}
          onClick={() => handleSelect(opt.value)}
        >
          {t(`search:${opt.label}`)}
        </OptionButton>
      ))}
      <Box
        sx={{ borderTop: `1px solid ${c.inputBorder}`, mt: "4px", pt: "4px" }}
      >
        <Box
          component="button"
          onClick={handleClear}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            px: "10px",
            py: "6px",
            fontSize: "0.75rem",
            fontFamily: "inherit",
            color: c.muted,
            bgcolor: "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            "&:hover": { bgcolor: c.hover },
          }}
        >
          {t("common:clear")}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Site popover
// ---------------------------------------------------------------------------

function SitePopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode, customerId } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const { data: sitesData } = useSites(customerId);
  const sites = sitesData?.sites || [];
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(filters.siteIds || []),
  );

  const filtered = search
    ? sites.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : sites;

  const toggle = (siteId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const handleApply = () => {
    const arr = Array.from(selected);
    onFiltersChange({ ...filters, siteIds: arr.length > 0 ? arr : undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, siteIds: undefined });
    onClose();
  };

  return (
    <MultiSelectShell
      c={c}
      search={search}
      setSearch={setSearch}
      searchPlaceholder={t("search:searchSites")}
      onClear={handleClear}
      onApply={handleApply}
    >
      {filtered.map((s) => (
        <CheckboxRow
          key={s.siteId}
          c={c}
          checked={selected.has(s.siteId)}
          onChange={() => toggle(s.siteId)}
          label={s.name}
        />
      ))}
      {filtered.length === 0 && (
        <Box
          sx={{ px: "10px", py: "6px", fontSize: "0.75rem", color: c.muted }}
        >
          {t("common:noSitesFound")}
        </Box>
      )}
    </MultiSelectShell>
  );
}

// ---------------------------------------------------------------------------
// Tags multi-select popover
// ---------------------------------------------------------------------------

function TagsPopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode, customerId } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const { data: allTags } = useInteractionTags(customerId);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(filters.tags || []),
  );
  const [search, setSearch] = useState("");

  const tags = allTags || [];
  const filtered = search
    ? tags.filter((t) => t.toLowerCase().includes(search.toLowerCase()))
    : tags;

  const toggle = (tag: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleApply = () => {
    const arr = Array.from(selected);
    onFiltersChange({ ...filters, tags: arr.length > 0 ? arr : undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, tags: undefined });
    onClose();
  };

  return (
    <MultiSelectShell
      c={c}
      search={search}
      setSearch={setSearch}
      searchPlaceholder={t("search:searchTags")}
      onClear={handleClear}
      onApply={handleApply}
    >
      {filtered.map((tag) => (
        <CheckboxRow
          key={tag}
          c={c}
          checked={selected.has(tag)}
          onChange={() => toggle(tag)}
          label={tag}
        />
      ))}
      {filtered.length === 0 && (
        <Box
          sx={{
            px: "10px",
            py: "6px",
            fontSize: "0.75rem",
            color: c.textSecondary,
          }}
        >
          {t("common:noTagsFound")}
        </Box>
      )}
    </MultiSelectShell>
  );
}

// ---------------------------------------------------------------------------
// Text input popover (generic)
// ---------------------------------------------------------------------------

function TextPopover(props: PopoverWithChipDef) {
  const { chipDef, filters, onFiltersChange, onClose, mode, customerId } =
    props;
  const c = popoverColors(mode);
  const filterKey = chipDef.id as keyof SearchFilters;
  const [input, setInput] = useState((filters[filterKey] as string) || "");
  const getDisplayLabel = useFilterDisplayLabel(customerId);
  const displayLabel = getDisplayLabel(chipDef);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleApply = () => {
    onFiltersChange({ ...filters, [filterKey]: input || undefined });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({ ...filters, [filterKey]: undefined });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApply();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "14px",
        minWidth: 240,
      }}
    >
      <SectionLabel c={c}>{displayLabel}</SectionLabel>
      <Box
        ref={inputRef}
        component="input"
        type="text"
        value={input}
        placeholder={`Enter ${displayLabel.toLowerCase()}...`}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setInput(e.target.value)
        }
        onKeyDown={handleKeyDown}
        sx={inputSx(c)}
      />
      <PopoverFooter c={c} onClear={handleClear} onApply={handleApply} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Boolean popover (three-state: Yes / No / Any)
// ---------------------------------------------------------------------------

function BooleanPopover(props: PopoverWithChipDef) {
  const { chipDef, filters, onFiltersChange, onClose, mode } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const filterKey = chipDef.id as keyof SearchFilters;
  const current = filters[filterKey] as boolean | undefined;

  const handleSelect = (value: boolean | undefined) => {
    onFiltersChange({ ...filters, [filterKey]: value });
    onClose();
  };

  const options: { value: boolean | undefined; labelKey: string }[] = [
    { value: true, labelKey: "yes" },
    { value: false, labelKey: "no" },
    { value: undefined, labelKey: "any" },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        p: "8px",
        minWidth: 140,
      }}
    >
      {options.map((opt) => (
        <OptionButton
          key={opt.labelKey}
          c={c}
          isActive={current === opt.value}
          onClick={() => handleSelect(opt.value)}
        >
          {t(`common:${opt.labelKey}`)}
        </OptionButton>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Duration popover
// ---------------------------------------------------------------------------

function DurationPopover(props: PopoverBaseProps) {
  const { filters, onFiltersChange, onClose, mode } = props;
  const { t } = useTranslation(["search", "common"]);
  const c = popoverColors(mode);
  const [min, setMin] = useState(
    filters.minDuration != null ? String(filters.minDuration) : "",
  );
  const [max, setMax] = useState(
    filters.maxDuration != null ? String(filters.maxDuration) : "",
  );
  const minRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    minRef.current?.focus();
  }, []);

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      minDuration: min ? Number(min) : undefined,
      maxDuration: max ? Number(max) : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    onFiltersChange({
      ...filters,
      minDuration: undefined,
      maxDuration: undefined,
    });
    onClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        p: "14px",
        minWidth: 240,
      }}
    >
      <SectionLabel c={c}>{t("search:minSeconds")}</SectionLabel>
      <Box
        ref={minRef}
        component="input"
        type="number"
        min={0}
        value={min}
        placeholder="0"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setMin(e.target.value)
        }
        sx={inputSx(c)}
      />
      <SectionLabel c={c}>{t("search:maxSeconds")}</SectionLabel>
      <Box
        component="input"
        type="number"
        min={0}
        value={max}
        placeholder={t("search:noLimit")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setMax(e.target.value)
        }
        sx={inputSx(c)}
      />
      <PopoverFooter c={c} onClear={handleClear} onApply={handleApply} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher — returns the right popover content for a chip
// ---------------------------------------------------------------------------

export default function FilterPopoverContent(props: FilterPopoverContentProps) {
  const { chipDef, ...rest } = props;

  // Special-case by chip id for typeahead variants
  if (chipDef.id === "agent") return <AgentPopover {...rest} />;
  if (chipDef.id === "phoneNumber") return <PhonePopover {...rest} />;

  switch (chipDef.popoverType) {
    case "date":
      return <DatePopover {...rest} />;
    case "select":
      return <SelectPopover chipDef={chipDef} {...rest} />;
    case "site":
      return <SitePopover {...rest} />;
    case "tags":
      return <TagsPopover {...rest} />;
    case "text":
      return <TextPopover chipDef={chipDef} {...rest} />;
    case "boolean":
      return <BooleanPopover chipDef={chipDef} {...rest} />;
    case "duration":
      return <DurationPopover {...rest} />;
    case "retention":
      return <RetentionPopover {...rest} />;
    default:
      return <TextPopover chipDef={chipDef} {...rest} />;
  }
}
